<script setup lang="ts">
import type { AddressForm, CustomerDto, CustomerResponse } from '@/types';
import { onMounted, ref, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import local from '@/stores/local';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import ContactInfoComponent from '@/components/ContactInfoComponent.vue';
import AddressInfoComponent from '@/components/AddressInfoComponent.vue';
import { useCustomerStore } from '@/stores/customer';
import { storeToRefs } from 'pinia';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { useIframeStore } from '@/stores/iframe';
import { vIgnorePasswordManager } from '@/utils/ignorePasswordManager';

const customerStore = useCustomerStore()
const { agreement } = storeToRefs(useAgreementStore())
const { config } = storeToRefs(useConfigStore())
const iframeStore = useIframeStore()

const valid = ref(true)
const customerForm = ref<Partial<CustomerDto>>({})
const addressForm = ref<AddressForm>({})
const addressInfo = useTemplateRef('addressInfo')

// The email can be filled before or after the address, the found customer's address wins either way
function onCustomerFound(customer: CustomerResponse) {
  agreement.value.customerId = customer.id
  addressInfo.value?.useCustomerAddress(customer)
}

// Nothing found, the email changed or the contact "Ryd" was pressed. A found customer's address goes with them
function onCustomerNotFound() {
  agreement.value.customerId = undefined
  addressInfo.value?.clearCustomerAddress()
}

onBeforeRouteLeave(() => {
  customerStore.savedCustomer = customerForm.value as CustomerDto
  local.setItem('customerForm', JSON.parse(JSON.stringify(customerForm.value)))
  local.setItem('addressForm', JSON.parse(JSON.stringify(addressForm.value)))
})

onMounted(async () => {
  if (iframeStore.isIframeMode && iframeStore.agreementResponsible) {
    agreement.value.agreementresponsible = iframeStore.agreementResponsible
  }

  const savedCustomerForm = await local.getItem<Partial<CustomerDto>>('customerForm')
  const savedAddressForm = await local.getItem<AddressForm>('addressForm')

  if (savedCustomerForm) {
    customerForm.value = savedCustomerForm
  }

  if (savedAddressForm) {
    addressForm.value = savedAddressForm
  }
})
</script>

<template>
  <v-container>
    <!-- 1Password took the contact and address fields for a login -->
    <v-form v-model="valid" v-ignore-password-manager>
      <v-row>
        <v-col>
          <ContactInfoComponent
            v-model="customerForm"
            :locked="!!agreement.customerId"
            @found="onCustomerFound"
            @not-found="onCustomerNotFound"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <AddressInfoComponent
            ref="addressInfo"
            v-model:customer-form="customerForm"
            v-model:address-form="addressForm"
          />
        </v-col>
      </v-row>

      <v-row v-if="config?.options?.length && !iframeStore.isIframeMode">
        <v-col>
          <v-select
            v-model="agreement.agreementresponsible"
            :items="config?.options"
            :label="config?.optionsLabel ?? 'Vælg venligst'"
            :disabled="false"
          />
        </v-col>
      </v-row>
    </v-form>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/addons" />
    <NextComponent
      to="/agreement/confirm"
      :disabled="!valid || !(addressForm.selected || addressForm.fromCustomer) ||(!iframeStore.isIframeMode && !!config?.options?.length && !agreement.agreementresponsible)"
    />
  </BottomBarComponent>
</template>
